import { useCallback, useEffect, useState } from 'react';
import type { IssuedKitItem, Station } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useKitDraftAutosave,
  type KitDraftLoad,
} from './useKitDraftSync';
import type { ConfirmRow } from './useKitConfirmItems';

interface KitDrawerDraftInput {
  personId: string;
  station: Station;
  /** The already-fetched draft; the item hooks need it before this runs. */
  load: KitDraftLoad;
  items: IssuedKitItem[];
  assetCategoryById: Record<string, string | null>;
  confirmRows: ConfirmRow[];
  /** True once the item and confirm lists have finished seeding. */
  listsSeeded: boolean;
}

/**
 * Ties the loaded draft to the autosave for the drawer: owns the desk notes
 * (the one part of the payload with no list hook of its own), decides when
 * everything has finished seeding, and packages up what the banner needs.
 *
 * Split from the drawer so the drawer stays about issuing kit; split from
 * useKitDraftLoad because the load has to happen before the item hooks run
 * and this has to happen after, with their state in hand.
 */
export function useKitDrawerDraft(input: KitDrawerDraftInput) {
  const { load } = input;
  const [notes, setNotes] = useState('');

  // Seeding is tracked by the nonce it happened at rather than a boolean, so
  // it reads as un-seeded in the very render a reload starts. See the same
  // pattern in useKitIssueItems for why the one-render lag matters.
  const [notesSeededNonce, setNotesSeededNonce] = useState<number | null>(null);
  const notesSeeded = notesSeededNonce === load.seed.nonce;
  useEffect(() => {
    if (notesSeeded || !load.seed.ready) return;
    setNotes(load.notes ?? '');
    setNotesSeededNonce(load.seed.nonce);
  }, [notesSeeded, load.seed.ready, load.seed.nonce, load.notes]);

  const autosave = useKitDraftAutosave({
    personId: input.personId,
    station: input.station,
    // Every part of the drawer must have seeded first, or the baseline is
    // taken against a half-filled drawer and the rest of the seed reads as
    // the operator's first edit.
    ready: load.seed.ready && input.listsSeeded && notesSeeded,
    items: input.items,
    assetCategoryById: input.assetCategoryById,
    confirmRows: input.confirmRows,
    notes,
    clientEventId: load.clientEventId,
    initialVersion: load.version,
    seedNonce: load.seed.nonce,
  });

  const currentUser = useCurrentUser();

  const discardAndReset = useCallback(async () => {
    await autosave.discardDraft();
    await load.reload();
  }, [autosave, load]);

  return {
    notes,
    setNotes,
    markSubmitted: autosave.markSubmitted,
    bannerProps: {
      pickedUpFrom: load.pickedUpFrom,
      pickedUpAt: load.pickedUpAt,
      isOwn: !!load.pickedUpBy && load.pickedUpBy === currentUser.data?.id,
      saveState: autosave.saveState,
      conflictMessage: autosave.conflictMessage,
      onReload: () => void load.reload(),
      onDiscard: () => void discardAndReset(),
      isDiscarding: autosave.isDiscarding,
    },
  };
}
